import React, { useState } from 'react';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';

// The client's tutorial video for interpreters (https://youtu.be/4bTSX-PM2vc).
// youtube-nocookie = YouTube's privacy-enhanced host; rel=0 keeps "related videos" to this channel.
const TUTORIAL_VIDEO_ID = '4bTSX-PM2vc';
const EMBED_URL = `https://www.youtube-nocookie.com/embed/${TUTORIAL_VIDEO_ID}?rel=0`;

/**
 * The pulsing "Help" pill in the interpreter portal's top bar, and the popup that plays the
 * tutorial video. Self-contained: styles are scoped to .help-video-* classes and injected here,
 * so nothing global changes.
 *
 * `renderTrigger` (optional) replaces the default pill with any element — used by the navbar
 * dropdown's Help item so both open the same modal.
 */
function HelpVideoButton({ renderTrigger }) {
  const [isOpen, setIsOpen] = useState(false);

  const open = (e) => {
    e?.preventDefault?.();
    setIsOpen(true);
  };
  const close = () => setIsOpen(false);

  return (
    <>
      <style>{`
        .help-video-btn {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          border: none;
          border-radius: 999px;
          padding: 8px 16px;
          background: #3176a0;
          color: #fff;
          font-weight: 600;
          font-size: 14px;
          cursor: pointer;
          animation: help-video-pulse 2.2s ease-out infinite;
        }
        .help-video-btn i {
          animation: help-video-wiggle 4s ease-in-out infinite;
        }
        .help-video-btn:hover {
          background: #285f81;
          animation-play-state: paused;
        }
        @keyframes help-video-pulse {
          0%   { box-shadow: 0 0 0 0 rgba(49, 118, 160, 0.55); }
          70%  { box-shadow: 0 0 0 12px rgba(49, 118, 160, 0); }
          100% { box-shadow: 0 0 0 0 rgba(49, 118, 160, 0); }
        }
        @keyframes help-video-wiggle {
          0%, 86%, 100% { transform: rotate(0deg); }
          90%  { transform: rotate(-14deg); }
          94%  { transform: rotate(12deg); }
          98%  { transform: rotate(-6deg); }
        }
        @media (prefers-reduced-motion: reduce) {
          .help-video-btn, .help-video-btn i { animation: none; }
        }
        .help-video-frame {
          position: relative;
          width: 100%;
          padding-top: 56.25%; /* 16:9 */
          background: #000;
          border-radius: 8px;
          overflow: hidden;
        }
        .help-video-frame iframe {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          border: 0;
        }
      `}</style>

      {renderTrigger ? (
        renderTrigger(open)
      ) : (
        <button type="button" className="help-video-btn" onClick={open} title="Watch the portal tutorial">
          <i className="fa-solid fa-circle-question"></i>
          Help
        </button>
      )}

      <Modal isOpen={isOpen} toggle={close} centered size="lg">
        <ModalHeader className="header_modal align-items-center" toggle={close}>
          <h5 className="modal-title_u">How to use the Interpreter Portal</h5>
        </ModalHeader>
        <ModalBody>
          <div className="help-video-frame">
            {/* mounted only while open, so closing the popup always stops the audio */}
            {isOpen && (
              <iframe
                src={EMBED_URL}
                title="Interpreter Portal Tutorial"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            )}
          </div>
        </ModalBody>
      </Modal>
    </>
  );
}

export default HelpVideoButton;
